import { type Line, type LineAudio, type Scene, speakersOf } from './manifest'

// Turns a scene into a playback timeline. Pure: the audio player schedules
// these steps on an AudioContext; the screen uses them to highlight the
// current line. All times are in ms on the scene's own clock (speed applied).

/**
 * listen    — everyone's lines play, yours included.
 * step      — others play up to your cue, then the scene waits until you say "next".
 * handsfree — your lines are timed silences; the scene never stops (car, commute).
 */
export type Mode = 'listen' | 'step' | 'handsfree'
export const MODES: readonly Mode[] = ['step', 'handsfree', 'listen']

export type PlanOptions = {
  mode: Mode
  /** Characters the actor plays — muted in step and hands-free modes. */
  myCharacterIds: ReadonlySet<string>
  /** Playback rate for other characters' audio and gaps (0.5–2). */
  speed?: number
  /** Multiplier on the time allowed to say your own line (hands-free mode). */
  myLineAllowance?: number
}

export type StepKind =
  /** Play this audio. */
  | 'audio'
  /** The actor's line: a stop (step mode) or a silence sized for them to say it (hands-free). */
  | 'mine'
  /** A spoken line with no audio yet: silence sized to its text, so the scene keeps moving. */
  | 'silent'
  /** Stage direction or sound cue: shown, takes no time. */
  | 'direction'

export type Step = {
  lineId: string
  index: number
  kind: StepKind
  startMs: number
  durationMs: number
  audio: LineAudio | null
  /** Rate to play audio at (speed); 1 for non-audio steps. */
  rate: number
}

export type Timeline = { steps: Step[]; totalMs: number }

const WORDS_PER_SECOND = 2.6 // ~155 wpm, conversational stage delivery
const MIN_SPOKEN_MS = 600

export function estimateSpeechMs(text: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length
  return Math.max(MIN_SPOKEN_MS, Math.round((words / WORDS_PER_SECOND) * 1000))
}

export function isSpoken(line: Line): boolean {
  return line.kind === 'dialogue' || line.kind === 'song'
}

/** Yours if you're any of its speakers — shared lines ("MEG/MOTHER:") belong to each of them. */
export function isMine(line: Line, myCharacterIds: ReadonlySet<string>): boolean {
  return speakersOf(line).some((id) => myCharacterIds.has(id))
}

export function planScene(scene: Scene, options: PlanOptions): Timeline {
  const speed = clamp(options.speed ?? 1, 0.5, 2)
  const allowance = options.myLineAllowance ?? 1.2
  const steps: Step[] = []
  let cursor = 0 // when the previous spoken line ends
  let previousWasAudio = false

  scene.lines.forEach((line, index) => {
    if (!isSpoken(line)) {
      steps.push({ lineId: line.id, index, kind: 'direction', startMs: cursor, durationMs: 0, audio: null, rate: 1 })
      return
    }

    const gap = line.timing.gapBeforeMs / speed
    // Overlap only makes sense when cutting into audio that is actually playing.
    const overlap = previousWasAudio ? line.timing.overlapMs / speed : 0
    const startMs = Math.max(0, cursor + gap - overlap)

    let kind: StepKind
    let durationMs: number
    if (options.mode !== 'listen' && isMine(line, options.myCharacterIds)) {
      kind = 'mine'
      // Step mode: the clock stops here until the actor taps next, so the line
      // takes no time. Hands-free: a silence sized from the line's recorded
      // length when there is one, else from its word count.
      durationMs = options.mode === 'step' ? 0 : (line.audio?.durationMs ?? estimateSpeechMs(line.text)) * allowance
    } else if (line.audio) {
      kind = 'audio'
      durationMs = line.audio.durationMs / speed
    } else {
      kind = 'silent'
      durationMs = estimateSpeechMs(line.text) / speed
    }

    durationMs = Math.round(durationMs)
    steps.push({
      lineId: line.id,
      index,
      kind,
      startMs: Math.round(startMs),
      durationMs,
      audio: kind === 'audio' ? line.audio : null,
      rate: kind === 'audio' ? speed : 1,
    })
    cursor = Math.max(cursor, startMs + durationMs)
    previousWasAudio = kind === 'audio'
  })

  return { steps, totalMs: Math.round(cursor) }
}

/** In step mode, the next of the actor's lines at or after `fromMs` — where playback must stop and wait. */
export function nextStop(timeline: Timeline, fromMs: number): Step | undefined {
  return timeline.steps.find((s) => s.kind === 'mine' && s.startMs >= fromMs)
}

/** The step playing at `positionMs`: the last one that has started. Directions win ties so they get shown. */
export function stepAt(timeline: Timeline, positionMs: number): Step | undefined {
  let found: Step | undefined
  for (const step of timeline.steps) {
    if (step.startMs > positionMs) break
    found = step
  }
  return found
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n))
}
